import type {
  LoginRequest,
  LoginResponse,
  User,
  Case,
  CreateCaseRequest,
  PaginatedResponse,
  CaseRecord,
} from "../../shared/types";

const BASE_URL = "/api";

class ApiService {
  private token: string | null = null;

  constructor() {
    this.token = localStorage.getItem("token");
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem("token", token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem("token");
  }

  private async request<T>(
    path: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...((options.headers as Record<string, string>) || {}),
    };

    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${BASE_URL}${path}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "请求失败" }));
      throw new Error(error.error || "请求失败");
    }

    return response.json();
  }

  // 认证相关
  async login(data: LoginRequest): Promise<LoginResponse> {
    const result = await this.request<LoginResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify(data),
    });
    this.setToken(result.token);
    return result;
  }

  async getMe(): Promise<User> {
    return this.request<User>("/auth/me");
  }

  async getUsers(): Promise<User[]> {
    return this.request<User[]>("/auth/users");
  }

  logout() {
    this.clearToken();
  }

  // 案件相关
  async getCases(params?: {
    status?: string;
    keyword?: string;
    page?: number;
    pageSize?: number;
  }): Promise<PaginatedResponse<Case>> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set("status", params.status);
    if (params?.keyword) searchParams.set("keyword", params.keyword);
    if (params?.page) searchParams.set("page", params.page.toString());
    if (params?.pageSize)
      searchParams.set("pageSize", params.pageSize.toString());

    const query = searchParams.toString();
    return this.request<PaginatedResponse<Case>>(`/cases?${query}`);
  }

  async getCase(id: number): Promise<Case> {
    return this.request<Case>(`/cases/${id}`);
  }

  async createCase(data: CreateCaseRequest): Promise<{ id: number; caseNo: string }> {
    return this.request<{ id: number; caseNo: string }>("/cases", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateCase(
    id: number,
    data: Partial<CreateCaseRequest>
  ): Promise<void> {
    await this.request(`/cases/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async pushCase(id: number, assignedTo: number): Promise<void> {
    await this.request(`/cases/${id}/push`, {
      method: "POST",
      body: JSON.stringify({ assignedTo }),
    });
  }

  async archiveCase(id: number): Promise<void> {
    await this.request(`/cases/${id}/archive`, {
      method: "POST",
    });
  }

  // 记录相关
  async addRecord(
    caseId: number,
    recordType: string,
    content: string
  ): Promise<{ id: number }> {
    return this.request<{ id: number }>(`/records/${caseId}`, {
      method: "POST",
      body: JSON.stringify({ recordType, content }),
    });
  }

  // 文件上传
  async uploadFile(
    caseId: number,
    file: File,
    category: string = "other"
  ): Promise<{ id: number; fileKey: string; fileName: string }> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("caseId", caseId.toString());
    formData.append("category", category);

    const response = await fetch(`${BASE_URL}/upload`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${this.token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "上传失败" }));
      throw new Error(error.error || "上传失败");
    }

    return response.json();
  }

  async deleteFile(id: number): Promise<void> {
    await this.request(`/upload/${id}`, {
      method: "DELETE",
    });
  }

  getFileUrl(fileKey: string): string {
    return `${BASE_URL}/upload/${fileKey}`;
  }
}

export const api = new ApiService();
